<template>
  <!-- Replacement Quote (order) detail — no Figma frame; built like Service & Repair Job Details.
       Flow and actions follow the Android technician app (OrderLandingFragment); APIs are api/v3/pro/orders/*. -->
  <div class="w-full font-roboto">
    <div v-if="order"
      class="w-full relative border-1 rounded-2xl px-[10px] laptop:px-[20px] pb-[10px] laptop:pb-[25px] largeDesktop:px-[30px] largeDesktop:pb-[35px] pt-[35px] border-gray-300">
      <DetailBackButton fallback="/work-order/replacement-quote" />
      <!-- actions: first 3 as pills, the rest in the ⋮ menu (ActionBar), on the card's top edge like Job Details -->
      <ActionBar v-if="actions.length" :actions="actions"
        trigger-class="group w-[40px] h-[40px] rounded-full flex items-center justify-center transition-all duration-300 border-0 bg-transparent p-0"
        class="w-full laptop:px-2.5 absolute -top-[21px] right-0">
        <template #pill="{ action, run, narrow }">
          <button type="button" :disabled="action.disabled"
            class="group flex items-center rounded-full gap-2 py-1.5 pl-3 pr-2 font-roboto font-normal text-[15px] largeDesktop:text-[17px] h-[40px] border-1 whitespace-nowrap transition duration-300 disabled:cursor-not-allowed disabled:opacity-[0.5]"
            :class="narrow ? 'bg-[color:var(--pill)] border-[color:var(--pill)] text-[#fff]' : 'bg-[#fff] border-[color:var(--pill)] text-[color:var(--pill)] enabled:hover:bg-[color:var(--pill)] enabled:hover:text-[#fff]'"
            :style="{ '--pill': action.color }" @click="run(action)">
            {{ action.label }}
            <span
              class="w-[26px] h-[26px] rounded-full flex items-center justify-center bg-white shadow-[0px_0px_15px_-7px_#333]">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M0 0h24v24H0z" fill="none" />
                <path fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                  :stroke="action.color" d="M4 12H6.5M14 18L20 12L14 6M20 12H9.5" />
              </svg>
            </span>
          </button>
        </template>
        <template #trigger>
          <img src="../../assets/images/3-dots-icon.png" alt=""
            class="w-full h-full object-contain group-hover:hidden block [@media(max-width:1023px)]:!hidden">
          <img src="../../assets/images/3-dots-icon-hover.png" alt=""
            class="w-full h-full object-contain group-hover:block hidden [@media(max-width:1023px)]:!block">
        </template>
      </ActionBar>

      <!-- status 0: job request -->
      <div v-if="order.status == RQ_STATUS.PENDING && isOwner"
        class="flex mobile:flex-col tablet:flex-row tablet:items-center justify-between gap-3 rounded-[10px] p-3 mb-4 bg-[#FEF7F7] border-1 border-[#F67280]">
        <div class="min-w-0">
          <h3 class="m-0 text-[18px] font-medium text-[#333333]">New replacement quote request</h3>
          <p class="m-0 mt-1 text-[14px] text-[#333333]">PerfectMatch schedule: {{ formatDate(order.inspection_datetime) }}
            <template v-if="order.job_count"> · You already have {{ order.job_count }} job(s) on this date</template>
          </p>
        </div>
        <div class="flex gap-[10px] flex-shrink-0">
          <button type="button" class="fxDeclineCancel" @click="runAction('reject')">Reject</button>
          <button type="button" class="fxBtnPrimary" @click="runAction('accept')">Accept</button>
        </div>
      </div>

      <!-- installation date needed (statuses 6–8) -->
      <div v-if="order.is_installation_datetime_required && isOwner && !readOnly"
        class="flex mobile:flex-col tablet:flex-row tablet:items-center justify-between gap-3 rounded-[12px] p-[14px] mb-[20px] border-1 border-[#C3DAFB] bg-[#EAF2FE]">
        <p class="m-0 text-[14px] text-[#1E4E8C]">Please set the installation date and time for this order.</p>
        <button type="button" class="fxBtnPrimary" @click="runAction('installDate')">Set Installation Date</button>
      </div>

      <div class="flex mobile:flex-col laptop:flex-row gap-[20px] items-start">
        <!-- left -->
        <div class="flex-1 min-w-0 w-full flex flex-col gap-[20px]">
          <RqOrderDetailsCard :order="order" :qr="qr" />

          <!-- Quote summary -->
          <div class="rqCard">
            <div class="flex flex-wrap items-center justify-between gap-2 pb-[10px]">
              <div class="flex items-center gap-1">
                <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
                <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
                <p class="text-[18px] laptop:text-[20px] font-semibold pl-2 m-0 text-[#333333]">Quote Summary</p>
              </div>
              <router-link :to="summaryPath" active-class=""
                class="inline-flex items-center h-[32px] px-[14px] rounded-[6px] border-1 border-[#7695FF] bg-[#fff] text-[13px] text-[#7695FF] no-underline hover:bg-[#EEF2FF]">View System Summary</router-link>
            </div>
            <!-- system (quick summary): name, location, price -->
            <div class="flex items-start justify-between gap-3 py-[12px] border-b-1 border-[#EFEFEF]">
              <div class="min-w-0">
                <span class="rqLabel">System</span>
                <p class="rqValue break-words">{{ systemName }}</p>
                <p v-if="quick?.location" class="m-0 mt-[2px] text-[12px] text-[#999999]">Location: {{ quick.location }}</p>
              </div>
              <span class="flex-shrink-0 text-[14px] font-medium text-[#333333]">{{ money(systemPrice) }}</span>
            </div>
            <!-- additional items (Submit PerfectMatch) -->
            <!-- line per item (qty × price = amount), then their own total; the heading carries no amount -->
            <div class="py-[12px] border-b-1 border-[#EFEFEF]">
              <span class="rqLabel">Additional Items</span>
              <p v-if="!(order.items || []).length" class="m-0 mt-[4px] text-[14px] text-[#999999]">No Additional Items added</p>
              <template v-else>
                <div v-for="item in order.items" :key="'i' + item.id"
                  class="flex items-start justify-between gap-3 mt-[8px] pl-[10px] border-l-2 border-[#EFEFEF]">
                  <div class="min-w-0">
                    <p class="m-0 text-[14px] text-[#333333] break-words">{{ item.title }}</p>
                    <p class="m-0 text-[12px] text-[#999999]">{{ item.quantity }} × {{ money(item.price) }}</p>
                  </div>
                  <span class="flex-shrink-0 text-[14px] text-[#333333]">{{ money(item.price * item.quantity) }}</span>
                </div>
                <div class="flex items-center justify-between gap-3 mt-[10px] pt-[8px] border-t border-dashed border-[#DDDDDD]">
                  <span class="text-[13px] font-medium text-[#666666]">Additional Items Total</span>
                  <span class="text-[14px] font-medium text-[#333333]">{{ money(itemsTotal) }}</span>
                </div>
              </template>
            </div>
            <div v-if="Number(order.preferred_payment_discount) > 0"
              class="flex items-center justify-between gap-3 py-[12px] border-b-1 border-[#EFEFEF]">
              <span class="rqLabel">Discount</span>
              <span class="text-[14px] font-medium text-[#1FA971]">-{{ money(order.preferred_payment_discount) }}</span>
            </div>
            <div v-if="paymentTypeLabel" class="flex items-center justify-between gap-3 py-[12px] border-b-1 border-[#EFEFEF]">
              <span class="rqLabel">Payment Type</span>
              <span class="text-[14px] text-[#333333]">{{ paymentTypeLabel }}</span>
            </div>
            <div v-if="quick?.notes" class="py-[12px] border-b-1 border-[#EFEFEF]">
              <span class="rqLabel">Notes</span>
              <p class="rqValue whitespace-pre-line">{{ quick.notes }}</p>
            </div>
            <div
              class="flex items-center justify-between gap-2 mt-[12px] px-[15px] py-[14px] rounded-[8px] bg-[#F3F4F7]">
              <span class="text-[13px] font-medium text-[#333333]">Total Amount</span>
              <span class="text-[18px] laptop:text-[20px] font-bold text-[#0E2F7E]">{{ money(quoteTotal) }}</span>
            </div>
          </div>

          <!-- Documents (Android "Documents" action: from Inspection Started, not cancelled / rejected) -->
          <RqDocumentsCard v-if="showDocuments" ref="docsCard" :order-id="orderId" :read-only="readOnly || isHelper"
            :can-upload="Number(order.status) !== RQ_STATUS.INSTALLATION_COMPLETED" />

          <!-- Invoices -->
          <div v-if="invoices.length" class="rqCard">
            <div class="flex items-center gap-1 pb-[10px]">
              <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
              <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
              <p class="text-[18px] laptop:text-[20px] font-semibold pl-2 m-0 text-[#333333]">Invoices</p>
            </div>
            <div v-for="(inv, index) in invoices" :key="'v' + index"
              class="flex items-center justify-between gap-3 py-[10px] border-b-1 border-[#EFEFEF] text-[13px]">
              <span class="text-[#333333]">{{ inv.title }}</span>
              <button type="button" class="text-[#7695FF] bg-transparent border-0 p-0"
                @click="openFrame(inv.title, inv.url)">View</button>
            </div>
          </div>
        </div>

        <!-- right -->
        <div class="w-full laptop:w-[320px] largeDesktop:w-[350px] laptop:flex-shrink-0 flex flex-col gap-[20px]">
          <RqConsumerCard :order="order" />

          <RqNotesCard ref="notesCard" :order-id="orderId" :can-write="canWriteNotes" />

          <MapCard v-if="Number(order.address?.lat)" :lat="order.address.lat" :lng="order.address.long"
            :address="order.address.address || ''" />

          <!-- QR -->
          <div v-if="qr?.qrcodeUrl" class="rqCard mobile:hidden laptop:block">
            <div class="flex items-center gap-1 pb-[15px]">
              <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
              <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
              <p class="text-[18px] laptop:text-[20px] font-semibold pl-2 m-0 text-[#333333]">QR Code</p>
            </div>
            <img :src="qr.qrcodeUrl" class="mx-auto block w-[140px] h-[140px] object-contain" alt="QR code">
            <p class="m-0 mt-2 text-[13px] text-[#333333] text-center">Present this QR code to the consumer to view the
              quote.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- one modal, content by type -->
    <div ref="rqModal" class="modal fade fxModal" :class="{ fxConfirm: isConfirm }" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable mobile:mx-[16px] tablet:mx-auto"
        :class="isPageModal ? 'max-w-full tablet:!max-w-[90vw] largeDesktop:!max-w-[1200px]' : (modal.type === 'submit' ? 'max-w-full tablet:!max-w-[640px]' : isConfirm ? 'max-w-full tablet:!max-w-[440px]' : 'max-w-full tablet:!max-w-[480px]')">
        <!-- a web page (System Summary …): 90% high, only the page scrolls, not the modal around it -->
        <div class="modal-content" :class="{ '!h-[90vh] !min-h-[90vh] !max-h-[90vh]': isPageModal }">
          <div class="modal-body !p-[15px]" :class="{ 'flex flex-col !overflow-hidden': isPageModal }">
            <FxModalHeader v-if="!isConfirm" :title="modalTitle" :dismiss="false" class="flex-shrink-0" @close="closeModal" />

            <!-- web page (invoice …) -->
            <template v-if="modal.type === 'frame'">
              <iframe v-if="modal.url" :src="modal.url" class="w-full flex-1 min-h-0 mt-[15px] border-0 rounded-[8px]"
                :title="modal.title" />
            </template>

            <!-- dispose / resume / not ready / final inspection: the same confirm popup as Suspend Job (S&R) -->
            <ConfirmLayout v-else-if="modal.type === 'dispose'" :title="modal.title" @close="closeModal">
              <div class="flex flex-col gap-[12px] text-left text-[14px] leading-normal">
                <p v-if="order.appointment_notes" class="m-0 text-[13px] text-[#666666] whitespace-pre-line">
                  <span class="font-medium text-[#333333]">Notes:</span> {{ order.appointment_notes }}</p>
                <div>
                  <span class="block mb-[5px] text-[13px] font-medium text-[#333333]">Please select a reason for disposing
                    of this lead <span class="text-[#EE5851]">*</span></span>
                  <FxSelect v-model="form.reasonId" :options="disposeReasons" label-key="name" value-key="id"
                    placeholder="Select a reason" inline @change="form.error = ''" />
                </div>
                <!-- Android: the text box only for an "other" reason, then required -->
                <div v-if="selectedReasonIsOther" class="m-0">
                  <span class="block mb-[5px] text-[13px] font-medium text-[#333333]">Please mention reason <span
                      class="text-[#EE5851]">*</span></span>
                  <textarea v-model="form.notes" maxlength="1000" rows="3" placeholder="Enter text here..."
                    class="w-full px-[12px] py-[10px] rounded-[10px] border-1 border-[#EFEFEF] outline-none text-[14px] text-[#333333] resize-none"
                    @input="form.error = ''" />
                </div>
                <p v-if="form.error" class="m-0 text-[12px] text-[#EE5851]">{{ form.error }}</p>
              </div>
              <template #actions>
                <button type="button" class="fxConfirmBtnOutline" @click="closeModal">Cancel</button>
                <button type="button" class="fxConfirmBtn" :disabled="busy" @click="submitDispose">Confirm</button>
              </template>
            </ConfirmLayout>

            <ConfirmLayout v-else-if="modal.type === 'resume' || modal.type === 'notReady'" :title="modal.title" @close="closeModal">
              <label class="block m-0 text-left text-[14px] leading-normal">
                <span class="block mb-[10px]">{{ modal.type === 'resume' ? 'Add a note on why this order is resumed.' :
                  'Tell us why the job is not ready for final inspection.' }}</span>
                <textarea v-model="form.notes" maxlength="1000" rows="4" placeholder="Notes"
                  class="w-full px-[12px] py-[10px] rounded-[10px] border-1 border-[#EFEFEF] outline-none text-[14px] text-[#333333] resize-none" />
              </label>
              <template #actions>
                <button type="button" class="fxConfirmBtnOutline" @click="closeModal">Cancel</button>
                <button type="button" class="fxConfirmBtn" :disabled="busy || !form.notes.trim()"
                  @click="modal.type === 'resume' ? submitResume() : submitNotReady()">Submit</button>
              </template>
            </ConfirmLayout>

            <ConfirmLayout v-else-if="modal.type === 'finalInspection'" :title="modal.title" @close="closeModal">
              Is the job ready for final inspection?
              <template #actions>
                <button type="button" class="fxConfirmBtnOutline" @click="openForm('notReady', 'Not Ready')">No</button>
                <button type="button" class="fxConfirmBtn" :disabled="busy" @click="submitReady">Yes</button>
              </template>
            </ConfirmLayout>

            <!-- send proposal / resend checkout link -->
            <template v-else-if="modal.type === 'proposal' || modal.type === 'resend'">
              <div class="flex flex-col gap-[10px] mt-[15px]">
                <label class="rqLabel" for="rqEmail">Email</label>
                <input id="rqEmail" v-model="form.email" type="email" class="rqInput" placeholder="consumer@email.com">
                <template v-if="modal.type === 'resend'">
                  <label class="rqLabel" for="rqPhone">Phone</label>
                  <input id="rqPhone" v-model="form.phone" type="tel" class="rqInput" placeholder="Phone number">
                </template>
              </div>
              <div class="flex justify-center gap-[10px] mt-[20px]">
                <button type="button" class="fxDeclineCancel" @click="closeModal">Cancel</button>
                <button type="button" class="fxBtnPrimary"
                  :disabled="busy || !validEmail(form.email) && !(modal.type === 'resend' && form.phone)"
                  @click="modal.type === 'proposal' ? submitProposal() : submitResend()">Send</button>
              </div>
            </template>

            <!-- installation date -->
            <template v-else-if="modal.type === 'installDate'">
              <label class="rqLabel mt-[15px]" for="rqInstallDate">Installation date and time</label>
              <!-- same date picker as everywhere (UQ equipment look); 15-minute steps, nothing in the past -->
              <date-picker id="rqInstallDate" v-model:value="form.datetime" class="rqDate mt-[6px]" type="datetime"
                format="MM/DD/YYYY hh:mm A" placeholder="Select Date and Time" :editable="false" :clearable="false"
                :confirm="true" :show-second="false" :minute-step="15" :disabled-date="beforeToday"
                :disabled-time="beforeNow" :append-to-body="false" />
              <div class="flex justify-center gap-[10px] mt-[20px]">
                <button type="button" class="fxDeclineCancel" @click="closeModal">Cancel</button>
                <button type="button" class="fxBtnPrimary" :disabled="busy || !form.datetime"
                  @click="submitInstallDate">Save</button>
              </div>
            </template>

            <!-- vendor equipment order -->
            <template v-else-if="modal.type === 'vendor'">
              <div v-if="vendor" class="flex flex-col mt-[15px] text-[13px] text-[#333333] px-[10px] tablet:px-0">
                <p class="m-0 flex flex-col py-[8px] border-b border-[#efefef]"><span class="text-[#999999]">Order Number:</span> {{ vendor.rq_id }}</p>
                <p class="m-0 flex flex-col py-[8px] border-b border-[#efefef]"><span class="text-[#999999]">Title:</span> {{ vendor.product_title || '-' }}</p>
                <p class="m-0 flex flex-col py-[8px] border-b border-[#efefef]"><span class="text-[#999999]">AHRI:</span> {{ vendor.ahri || '-' }}</p>
                <p class="m-0 flex flex-col py-[8px] border-b border-[#efefef]"><span class="text-[#999999]">Company Name:</span> {{ vendor.company_name || '-' }}</p>
                <p class="m-0 flex flex-col py-[8px] border-b border-[#efefef]"><span class="text-[#999999]">Vendor Email:</span> {{ vendor.vendor_email || '-' }}</p>
                <label class="flex items-start gap-[8px] mt-[12px] cursor-pointer">
                  <input v-model="form.confirm" type="checkbox" class="mt-[4px]">By clicking "Order Now", an order placement email will be sent to {{ vendor.vendor_email }} with the details provided above.
                </label>
              </div>
              <div class="flex justify-center gap-[10px] mt-[20px] mb-[10px] tablet:mb-0 px-[10px] tablet:px-0">
                <button type="button" class="fxDeclineCancel" @click="closeModal">Cancel</button>
                <button type="button" class="fxBtnPrimary" :disabled="busy || !form.confirm" @click="submitVendor">Order
                  Now</button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
    <!-- Android parity screens (src/components/replacementQuote) -->
    <template v-if="order">
      <RqCompleteInstallationModal ref="complete" :order-id="orderId"
        @completed="res => applyResult(res, 'Your unit Installation has been completed')" />
      <RqDiscountModal ref="discount" :order-id="orderId" @changed="loadAll" />
      <RqSystemScannerModal ref="scanner" :order-id="orderId" :order-type="order.type"
        :editable="!readOnly && Number(order.status) < RQ_STATUS.INSTALLATION_COMPLETED" @changed="loadAll" />
      <RqPaymentAckModal ref="paymentAck" :order-id="orderId" :consumer-name="order.consumer?.first_name || order.consumer?.full_name || ''"
        @changed="loadAll" />
    </template>
  </div>
</template>

<script>
import DetailBackButton from "@/components/partialComponent/DetailBackButton.vue";
import { Modal } from "bootstrap";
import moment from "moment";
import DatePicker from "vue-datepicker-next";
import "vue-datepicker-next/index.css";
import { useToast } from "vue-toastification";
import FxModalHeader from "../../components/nest_components/FxModalHeader.vue";
import ConfirmLayout from "../../components/confirmDialog/ConfirmLayout.vue";
import ActionBar from "../../components/partialComponent/ActionBar.vue";
import FxSelect from "../../components/partialComponent/FxSelect.vue";
import RqOrderDetailsCard from "../../components/replacementQuote/RqOrderDetailsCard.vue";
import RqConsumerCard from "../../components/replacementQuote/RqConsumerCard.vue";
import MapCard from "../../components/partialComponent/MapCard.vue";
import RqNotesCard from "../../components/replacementQuote/RqNotesCard.vue";
import RqDocumentsCard from "../../components/replacementQuote/RqDocumentsCard.vue";
import RqCompleteInstallationModal from "../../components/replacementQuote/RqCompleteInstallationModal.vue";
import RqDiscountModal from "../../components/replacementQuote/RqDiscountModal.vue";
import RqSystemScannerModal from "../../components/replacementQuote/RqSystemScannerModal.vue";
import RqPaymentAckModal from "../../components/replacementQuote/RqPaymentAckModal.vue";
import { rqApi, RQ_STATUS } from "../../services/rqApi";
import { isSilentLogin } from "@/helpers/embedMode";
const toast = useToast();

const BLUE = "#7695FF", TEAL = "#00B7C2", PINK = "#F67280", GREEN = "#1FA971";

export default {
  components: {
    DatePicker,
    DetailBackButton, FxModalHeader, ConfirmLayout, ActionBar, RqOrderDetailsCard, RqConsumerCard, RqNotesCard, MapCard, FxSelect, RqDocumentsCard,
    RqCompleteInstallationModal, RqDiscountModal, RqSystemScannerModal, RqPaymentAckModal },
  data() {
    return {
      RQ_STATUS,
      order: null,
      quick: null,
      invoices: [],
      qr: null,
      vendor: null,
      disposeReasons: [],
      busy: false,
      isLaptop: window.innerWidth >= 1024,
      modal: { type: "", title: "", url: "", editable: false },
      form: {},
      defaultAvatar: "https://t3.ftcdn.net/jpg/03/46/83/96/360_F_346839683_6nAPzbhpSkIpb8pmAwufkC7c5eD7wYws.jpg",
      myUserId: JSON.parse(localStorage.getItem("user") || "{}")?.user?.user_id,
    };
  },
  computed: {
    // small "Are you sure …" style popups share the confirm look (ConfirmLayout)
    isConfirm() {
      return ["dispose", "resume", "notReady", "finalInspection"].includes(this.modal.type);
    },
    summaryPath() {
      return `/replacement-quote/summary/${this.orderId}`;
    },
    orderId() {
      return this.$route.params.id;
    },
    // the tech on the order (sales person / installer / assigned tech)
    isOwner() {
      const o = this.order || {};
      return [o.technician?.user_id, o.seller_id, o.installer_id].map(String).includes(String(this.myUserId));
    },
    isHelper() {
      return this.order && String(this.order.helper_id) === String(this.myUserId);
    },
    readOnly() {
      return !this.isOwner;
    },
    // Quote Summary. parts_total is only sent during the inspection (status 4 / 5, Laravel
    // DirectOrder::getLineItemsPartsTotalDollarAttribute); after Submit PerfectMatch the additional items are inside
    // `total`. So: total = total + parts_total − discount (Android FinalizeYourQuotation) and system = total − items.
    // quick summary name is "title - tonnage ton - seer seer - name" and title is often the name again: drop repeats
    systemName() {
      const parts = String(this.quick?.name || "").split(" - ").map(p => p.trim()).filter(Boolean);
      return [...new Set(parts)].join(" - ") || this.order?.type || "-";
    },
    itemsTotal() {
      return (this.order?.items || []).reduce((t, i) => t + Number(i.price || 0) * Number(i.quantity || 0), 0);
    },
    systemPrice() {
      return this.quoteTotal + Number(this.order?.preferred_payment_discount || 0) - this.itemsTotal;
    },
    quoteTotal() {
      const o = this.order || {};
      return Number(o.total || 0) + Number(o.parts_total || 0) - Number(o.preferred_payment_discount || 0);
    },
    paymentTypeLabel() {
      const p = this.order?.preferred_payment_type || {};
      const cap = (v) => String(v || "").replace(/[_-]/g, " ").replace(/\b\w/g, c => c.toUpperCase());
      if (!p.type) return "";
      return p.sub_type && p.sub_type !== p.type ? `${cap(p.type)} (${cap(p.sub_type)})` : cap(p.type);
    },
    showDocuments() {
      const s = Number(this.order?.status);
      return s >= RQ_STATUS.INSPECTION_STARTED && s !== RQ_STATUS.CANCEL && s !== RQ_STATUS.REJECTED;
    },
    // modals that show a web page (System Summary, invoice, data sheet, the Submit PerfectMatch summary step)
    isPageModal() {
      return this.modal.type === "frame";
    },
    // Submit PerfectMatch steps carry the Android screen titles
    modalTitle() {
      if (this.modal.type !== "submit") return this.modal.title;
      return this.modal.title;
    },
    // Android JobNotesFragment: 6 <= status < 16
    canWriteNotes() {
      const s = Number(this.order?.status);
      return !this.readOnly && s >= RQ_STATUS.INSTALLATION_PENDING && s < RQ_STATUS.CLOSED;
    },
    isInstaller() {
      return (this.order?.technician_type || "").split(",").includes("installer");
    },
    // Android OrderLandingFragment.setOrderButtons — ONE ordered list; ActionBar shows 3 pills, the rest in ⋮.
    // primary = the status action (moved first); danger (Pause Installation, Dispositioned Leads) goes last.
    actions() {
      const o = this.order;
      if (!o) return [];
      const s = Number(o.status);
      const list = [];
      const add = (key, label, color = BLUE, extra = {}) => list.push({ key, label, color, onClick: () => this.runAction(key), ...extra });
      const primary = { primary: true };
      add("summary", "System Summary", TEAL);
      if (this.isHelper) return list;
      if (this.readOnly) {
        if (s >= RQ_STATUS.INSPECTION_STARTED) add("dataSheet", "Data Sheet");
        return list;
      }
      if (s === RQ_STATUS.ACCEPTED) add("enRoute", "On My Way", PINK, primary);
      if (s === RQ_STATUS.EN_ROUTE) add("arrived", "Arrived", PINK, primary);
      if (s === RQ_STATUS.ARRIVED) add("startInspection", "Start PerfectMatch", PINK, primary);
      if (s === RQ_STATUS.INSPECTION_STARTED) add("submit", "Submit PerfectMatch", PINK, primary);
      if (s === RQ_STATUS.SUBMITTED) {
        add("checkout", "Checkout", GREEN, primary);
        if (!o.is_created_from_partner && !this.isInstaller) add("discount", "Add Discount");
        if (o.is_greensky) add("greensky", "GreenSky®");
        add("resend", "Resend Checkout Link");
      }
      if (this.isInstaller) {
        if (s === RQ_STATUS.INSTALLATION_PENDING) add("installStart", "Start Installation", PINK, primary);
        if (s === RQ_STATUS.INSTALLATION_STARTED) {
          add("installComplete", "Complete Installation", GREEN, primary);
          add("installPause", "Pause Installation", PINK, { variant: "danger" });
        }
        if (s === RQ_STATUS.INSTALLATION_PAUSE) add("installStart", "Resume Installation", PINK, primary);
      }
      if (s === RQ_STATUS.INSTALLATION_COMPLETED && !o.payment_outside_platform) add("finalInspection", "Ready For Final Inspection", GREEN, primary);
      if (s >= RQ_STATUS.INSPECTION_STARTED && s !== RQ_STATUS.CANCEL && s !== RQ_STATUS.REJECTED) {
        add("dataSheet", "Data Sheet");
      }
      if (s >= RQ_STATUS.INSTALLATION_PENDING && s !== RQ_STATUS.CANCEL) add("scanner", "System Scanner");
      if (Number(o.invoice_count) > 0) add("invoice", "View Invoice");
      if (Number(o.is_send_proposal) === 1) add("proposal", "Send Proposal");
      if (o.is_show_vendor_equipment_order_button) add("vendor", "Order Now");
      if (o.is_installation_datetime_required) add("installDate", "Set Installation Date");
      if (o.can_show_resume) add("resume", "Resume", GREEN);
      if (o.can_show_dispose) add("dispose", "Dispositioned Leads", "#D7385E", { variant: "danger" });
      return list;
    },
    selectedReasonIsOther() {
      return !!this.disposeReasons.find(r => r.id === this.form.reasonId)?.is_other;
    },
  },
  watch: {
    "$route.params.id"() {
      if (this.orderId) this.loadAll();
    },
  },
  mounted() {
    window.addEventListener("resize", this.onResize);
    this.loadAll();
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.onResize);
    if (this.bsModal) this.bsModal.hide();
  },
  methods: {
    beforeToday(date) {
      return date < moment().startOf("day").toDate();
    },
    beforeNow(date) {
      return date < new Date();
    },
    onResize() {
      this.isLaptop = window.innerWidth >= 1024;
    },
    money(value) {
      const n = Number(value || 0);
      return (n < 0 ? "-$" : "$") + Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    // API dates are unix seconds (as strings); local time, same as the RQ History list
    formatDate(ts) {
      if (!ts || Number(ts) <= 0) return "-";
      return moment.unix(Number(ts)).format("MM/DD/YYYY hh:mm A");
    },
    validEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || "");
    },
    // the list logs a silent login / SSO user out (router): they stay on this page
    toList() {
      if (!isSilentLogin()) this.$router.replace("/work-order/replacement-quote");
    },
    async loadAll() {
      const res = await rqApi.detail(this.orderId);
      if (!res.ok) {
        toast.error(res.message || "Replacement quote not found.");
        this.toList();
        return;
      }
      this.order = res.body;
      this.loadExtras();
      this.$nextTick(this.autoPopups);
    },
    async loadExtras() {
      const id = this.orderId;
      this.$refs.notesCard?.load();
      this.$refs.docsCard?.load();
      const [quick, invoices] = await Promise.all([
        rqApi.quickSummary(id),
        Number(this.order.invoice_count) > 0 ? rqApi.invoices(id) : Promise.resolve({ ok: false }),
      ]);
      this.quick = quick.ok ? quick.body : null;
      this.invoices = invoices.ok && Array.isArray(invoices.body) ? invoices.body : [];
      if (Number(this.order.status) >= RQ_STATUS.SUBMITTED) {
        const qr = await rqApi.qrCode(id);
        this.qr = qr.ok ? qr.body : null;
      }
    },
    // after a status call: the API returns the order; reload the cards too
    applyResult(res, successText) {
      if (res.ok) {
        if (res.body?.id) this.order = res.body;
        if (successText) toast.success(successText);
        this.loadExtras();
        return true;
      }
      this.showError(res);
      return false;
    },
    // single-active-record guard: offer to open the active job
    showError(res) {
      const record = res.activeRecord?.active_record;
      if (record) {
        this.$dialog.show({
          title: "Dispositioned Leads",
          text: res.activeRecord.message || res.message,
          buttonText: record.button_text || "Resolve",
          onConfirm: () => {
            if (record.type === "appointment") this.$router.push(`/appointment/detail/${record.id}`);
            else this.$router.push(`/replacement-quote/detail/${record.id}`);
          },
        });
        return;
      }
      toast.error(res.message || "Something went wrong.");
    },
    confirmThen(title, text, fn) {
      this.$dialog.show({ title, text, buttonText: "Yes", onConfirm: fn });
    },
    async simple(apiCall, successText) {
      this.busy = true;
      const res = await apiCall();
      this.busy = false;
      return this.applyResult(res, successText);
    },
    runAction(key) {
      const id = this.orderId;
      switch (key) {
        case "accept": return this.simple(() => rqApi.accept(id), "Replacement quote accepted.");
        case "reject": return this.confirmThen("Reject", "Are you sure you want to reject this replacement quote?", async () => {
          const res = await rqApi.reject(id);
          if (res.ok) {
            toast.success("Replacement quote rejected.");
            if (isSilentLogin()) this.loadAll();
            else this.toList();
          } else this.showError(res);
        });
        case "enRoute": return this.confirmThen("En Route", "Please confirm that you are on the way to conduct the PerfectMatch inspection?", () => this.simple(() => rqApi.enRoute(id), "Marked as en route."));
        case "arrived": return this.confirmThen("Arrived", "Are you sure you have arrived at job location?", () => this.simple(() => rqApi.arrived(id), "Marked as arrived."));
        case "startInspection": return this.confirmThen("Start PerfectMatch", "Are you sure you want to start PerfectMatch?", () => this.simple(() => rqApi.inspectionStart(id), "PerfectMatch started."));
        case "installStart": return this.order.status == RQ_STATUS.INSTALLATION_PAUSE
          ? this.confirmThen("Resume Installation", "Are you sure you want to resume unit Installation?", () => this.simple(() => rqApi.installationStart(id), "Installation resumed."))
          : this.confirmThen("Start Installation", "Are you sure you want to start new unit installation?", () => this.simple(() => rqApi.installationStart(id), "Installation started."));
        case "installPause": return this.confirmThen("Pause Installation", "Are you sure you want to pause unit Installation?", () => this.simple(() => rqApi.installationPause(id), "Installation paused."));
        case "summary": return this.$router.push(this.summaryPath);
        case "submit": return this.openSubmit();
        case "installComplete": return this.$refs.complete.open();
        case "finalInspection": return this.openForm("finalInspection", "Final Inspection");
        case "dispose": return this.openDispose();
        case "resume": return this.openForm("resume", "Resume Order");
        case "proposal": return this.openForm("proposal", "Send Proposal", { email: this.order.consumer?.email || "" });
        case "resend": return this.openForm("resend", "Resend Checkout Link", { email: this.order.consumer?.email || "", phone: this.order.consumer?.phone || "" });
        case "installDate": return this.openForm("installDate", "Installation Date", {
          datetime: Number(this.order.installation_datetime) > 0 ? moment.unix(Number(this.order.installation_datetime)).toDate() : null,
        });
        case "vendor": return this.openVendor();
        case "checkout": return this.checkout();
        case "invoice": return this.openInvoice();
        case "dataSheet": return this.$router.push(`/replacement-quote/data-sheet/${id}`);
        case "discount": return this.$refs.discount.open();
        case "scanner": return this.$refs.scanner.open();
        case "greensky": return this.confirmGreensky();
      }
    },
    showModal() {
      this.$nextTick(() => {
        this.bsModal = this.bsModal || new Modal(this.$refs.rqModal);
        this.bsModal.show();
      });
    },
    closeModal() {
      if (this.bsModal) this.bsModal.hide();
      this.modal = { type: "", title: "", url: "", editable: false };
    },
    openForm(type, title, form = {}) {
      this.form = { notes: "", ...form };
      this.modal = { type, title, url: "", editable: false };
      this.showModal();
    },
    openFrame(title, url) {
      if (!url) return;
      this.modal = { type: "frame", title, url, editable: false };
      this.showModal();
    },
    async openDispose() {
      const res = await rqApi.disposeReasons(this.orderId);
      this.disposeReasons = res.ok && Array.isArray(res.body) ? res.body : [];
      this.openForm("dispose", "Dispositioned Leads", { reasonId: null, error: "" });
    },
    async submitDispose() {
      if (!this.form.reasonId || (this.selectedReasonIsOther && !this.form.notes.trim())) {
        this.form.error = "Reason field is required.";
        return;
      }
      this.busy = true;
      const res = await rqApi.dispose(this.orderId, this.form.reasonId, this.selectedReasonIsOther ? this.form.notes.trim() : "");
      this.busy = false;
      if (this.applyResult(res, "Lead has been disposed successfully.")) {
        this.closeModal();
        this.loadAll();
      }
    },
    async submitResume() {
      this.busy = true;
      const res = await rqApi.resume(this.orderId, this.form.notes.trim());
      this.busy = false;
      if (this.applyResult(res, "Order resumed.")) {
        this.closeModal();
        this.loadAll();
      }
    },
    async submitReady() {
      this.busy = true;
      const res = await rqApi.inspectionReady(this.orderId);
      this.busy = false;
      if (this.applyResult(res, "Marked ready for final inspection.")) this.closeModal();
    },
    async submitNotReady() {
      this.busy = true;
      const res = await rqApi.inspectionNotReady(this.orderId, this.form.notes.trim());
      this.busy = false;
      if (this.applyResult(res, "Notes sent.")) this.closeModal();
    },
    async submitProposal() {
      this.busy = true;
      const res = await rqApi.sendProposal(this.orderId, this.form.email.trim());
      this.busy = false;
      if (res.ok) {
        toast.success(res.message || "Proposal sent.");
        this.closeModal();
      } else this.showError(res);
    },
    async submitResend() {
      this.busy = true;
      const res = await rqApi.resendCheckoutLink(this.orderId, this.form.email.trim(), this.form.phone);
      this.busy = false;
      if (res.ok) {
        toast.success(res.message || "Checkout link sent.");
        this.closeModal();
      } else this.showError(res);
    },
    async submitInstallDate() {
      this.busy = true;
      const res = await rqApi.installationDatetime(this.orderId, moment(this.form.datetime).unix());
      this.busy = false;
      if (res.ok) {
        toast.success("Installation date saved.");
        this.closeModal();
        this.loadAll();
      } else this.showError(res);
    },
    async openVendor() {
      const res = await rqApi.vendorOrder(this.orderId);
      if (!res.ok) return this.showError(res);
      this.vendor = res.body;
      this.openForm("vendor", "Order Equipment", { confirm: false });
    },
    async submitVendor() {
      this.busy = true;
      const res = await rqApi.placeVendorOrder(this.vendor.rq_id);
      this.busy = false;
      if (res.ok) {
        toast.success(res.message || "Equipment order placed.");
        this.closeModal();
        this.loadAll();
      } else this.showError(res);
    },
    // Checkout: create the RQ invoice, then the existing Payment Method page
    async checkout() {
      this.busy = true;
      const res = await rqApi.createInvoice(this.orderId);
      this.busy = false;
      if (res.ok && res.body?.invoice_id) this.$router.push(`/checkout/invoice/${res.body.invoice_id}`);
      else this.showError(res);
    },
    async openInvoice() {
      if (!this.invoices.length) {
        const res = await rqApi.invoices(this.orderId);
        this.invoices = res.ok && Array.isArray(res.body) ? res.body : [];
      }
      if (this.invoices.length) this.openFrame(this.invoices[0].title || "Invoice", this.invoices[0].url);
      else toast.error("No invoice found.");
    },
    confirmGreensky() {
      const name = this.order.consumer?.first_name || "The consumer";
      this.$dialog.show({
        title: "Alert",
        text: `${name} has applied for GreenSky financing. After ${name} receives the shopping pass, you will be required to authorize the transactions for the shopping pass. Please consult with ${name} and then press "Proceed" to visit the GreenSky Merchant portal or press "Cancel" to return.`,
        buttonText: "Proceed",
        onConfirm: () => window.open("http://portal.greensky.com/", "_blank", "noopener"),
      });
    },
    // Android OrderLandingFragment after load: payment acknowledgement, then (once) the final inspection question
    autoPopups() {
      const o = this.order;
      if (this.readOnly || this.isHelper) return;
      if (o.acknowledgement === "REQUIRED" && !this.isInstaller) {
        this.$refs.paymentAck.open();
        return;
      }
      if (Number(o.status) === RQ_STATUS.INSTALLATION_COMPLETED && !o.has_inspection_notes && !Number(o.helper_id)
        && !o.payment_outside_platform && !this.finalInspectionAsked) {
        this.finalInspectionAsked = true;
        this.openForm("finalInspection", "Final Inspection");
      }
    },

    // Submit PerfectMatch (Android: additional items → finalize → payment type → inspection/submit)
    async openSubmit() {
      // Android: GET single-active-record { order_id } first; another active job → its dialog, no submit
      const guard = await rqApi.singleActiveRecord(this.orderId);
      if (guard.activeRecord) return this.showError(guard);
      // the System Summary page carries Add Additional Items / Finalize the quote (RqSubmitModal)
      this.$router.push(`${this.summaryPath}?submit=1`);
    },

  },
};
</script>

<style scoped>
.rqInput {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #E2E5EC;
  border-radius: 8px;
  font-size: 14px;
  color: #333333;
  outline: none;
  background: #fff;
}

.rqDate {
  position: relative;
  width: 100%;
}
.rqDate :deep(.mx-input) {
  height: 40px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #E1E5EF;
  font-size: 14px;
  color: #333333;
  box-shadow: none;
}
.rqDate :deep(.mx-datepicker-popup) {
  top: 100% !important;
  left: 0 !important;
  bottom: auto !important;
  margin-top: 4px;
  z-index: 20;
  max-width: calc(100vw - 32px);
  overflow-x: auto;
}
.rqInput:focus {
  border-color: #7695FF;
}
</style>
